import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { CSVLink } from "react-csv";
import { useAuth } from "../context/auth";
import PermissionGuard from "./PermissionGuard";
import ConfirmDialog from "./ConfirmDialog";

const BookmarkList = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    bookmarkId: null,
    customId: '',
    hasTracker: false,
    trackerCount: 0,
    trackerNames: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth(); // Get token from auth context

  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Vehicle type mapping
  const vehicleTypeMap = {
    1: "Motorcycle",
    2: "Tricycle",
  };

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchBookmarks = async () => {
      try {
        setLoading(true);
        setError("");

        // Check if user is authenticated
        if (!isAuthenticated || !token) {
          setError("Please login to view bookmarks");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${baseUrl}/auth/bookmarks`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: source.token,
        });

        if (isMounted) {
          setBookmarks(response.data);
        }
      } catch (error) {
        if (isMounted && !axios.isCancel(error)) {
          console.error("Error fetching bookmarks:", error);
          if (error.response?.status === 401) {
            setError("Authentication failed. Please login again.");
          } else {
            setError("Failed to load bookmarks. Please try again.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBookmarks();

    return () => {
      isMounted = false;
      source.cancel("Component unmounted. Request canceled.");
    };
  }, [baseUrl, token, isAuthenticated]);

  const handleAdd = () => navigate("/dashboard/addBookmark");
  const handleEdit = (id) => navigate(`/dashboard/editBookmark/${id}`);
  const handleProfile = (id) => navigate(`/dashboard/bookmarkProfiles/${id}`);

  const handleOpenDialog = async (id) => {
    if (!isAuthenticated || !token) {
      setError("Please login to delete bookmarks");
      return;
    }

    const bookmark = bookmarks.find(b => b.id === id);
    if (!bookmark) return;

    try {
      // Check if bookmark has linked trackers
      const trackersResponse = await axios.get(`${baseUrl}/api/trackers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const linkedTrackers = trackersResponse.data.trackers?.filter(
        t => t.customId === bookmark.customId
      ) || [];

      setDeleteDialog({
        open: true,
        bookmarkId: id,
        customId: bookmark.customId,
        hasTracker: linkedTrackers.length > 0,
        trackerCount: linkedTrackers.length,
        trackerNames: linkedTrackers.map(t => t.name)
      });
    } catch (error) {
      console.error("Error checking linked trackers:", error);
      // If check fails, still allow deletion but without tracker warning
      setDeleteDialog({
        open: true,
        bookmarkId: id,
        customId: bookmark.customId,
        hasTracker: false,
        trackerCount: 0,
        trackerNames: []
      });
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialog({
      open: false,
      bookmarkId: null,
      customId: '',
      hasTracker: false,
      trackerCount: 0,
      trackerNames: []
    });
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      // If bookmark has linked trackers, unlink them first
      if (deleteDialog.hasTracker) {
        const trackersResponse = await axios.get(`${baseUrl}/api/trackers`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const linkedTrackers = trackersResponse.data.trackers?.filter(
          t => t.customId === deleteDialog.customId
        ) || [];

        // Unlink all trackers by setting their customId to null
        for (const tracker of linkedTrackers) {
          await axios.put(
            `${baseUrl}/api/trackers/${tracker.id}`,
            { ...tracker, customId: null },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }

      // Now delete the bookmark
      await axios.delete(`${baseUrl}/auth/bookmarks/${deleteDialog.bookmarkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBookmarks(bookmarks.filter((b) => b.id !== deleteDialog.bookmarkId));
      handleCloseDialog();
    } catch (error) {
      console.error("Error deleting bookmark:", error);
      setError(error.response?.data?.error || "Failed to delete bookmark. Please try again.");
      setDeleteLoading(false);
    }
  };

  const filteredBookmarks = bookmarks.filter((bookmark) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      bookmark.customId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.ofirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.omiddleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.olastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.ocontact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.odistrict?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.oemgContact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.oaddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vbusinessName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      bookmark.vreceiptNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vvinNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vengineNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vkNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vlicensePlate
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      bookmark.vsellerContact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vmodel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vcolor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.vAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.opfirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.opmiddleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.oplastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.opContact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.opemgContact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bookmark.opdriverLicense
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      bookmark.opAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bookmark.vehicleType &&
        vehicleTypeMap[bookmark.vehicleType]
          .toLowerCase()
          .includes(searchLower))
    );
  });

  // Prepare CSV data with transformed vehicleType
  const csvData = filteredBookmarks.map((bookmark) => ({
    ...bookmark,
    vehicleType: vehicleTypeMap[bookmark.vehicleType] || "Unknown",
    ownerName: `${bookmark.ofirstName} ${bookmark.olastName}`,
    operatorName: `${bookmark.opfirstName} ${bookmark.oplastName}`,
  }));

  const headers = [
    { label: "Vehicle Type", key: "vehicleType" },
    { label: "User ID", key: "userId" },
    { label: "Custom ID", key: "customId" },
    { label: "Owner First Name", key: "ofirstName" },
    { label: "Owner Middle Name", key: "omiddleName" },
    { label: "Owner Last Name", key: "olastName" },
    { label: "Owner Contact", key: "ocontact" },
    { label: "Owner District", key: "odistrict" },
    { label: "Owner Emergency Contact", key: "oemgContact" },
    { label: "Owner Address", key: "oaddress" },
    { label: "Vehicle Business Name", key: "vbusinessName" },
    { label: "Vehicle Receipt No", key: "vreceiptNo" },
    { label: "Vehicle VIN No", key: "vvinNo" },
    { label: "Vehicle Engine No", key: "vengineNo" },
    { label: "Vehicle K No", key: "vkNo" },
    { label: "Vehicle License Plate", key: "vlicensePlate" },
    { label: "Seller Contact", key: "vsellerContact" },
    { label: "Model", key: "vmodel" },
    { label: "Vehicle Color", key: "vcolor" },
    { label: "Vehicle Address", key: "vAddress" },
    { label: "Operator First Name", key: "opfirstName" },
    { label: "Operator Middle Name", key: "opmiddleName" },
    { label: "Operator Last Name", key: "oplastName" },
    { label: "Operator Contact", key: "opContact" },
    { label: "Operator Emergency Contact", key: "opemgContact" },
    { label: "Operator Driver License", key: "opdriverLicense" },
    { label: "Operator Address", key: "opAddress" },
  ];

  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Bookmark Management
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Please login to view bookmarks.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Bookmark Management
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Bookmark Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <PermissionGuard resource="bookmark" action="create">
            <Button variant="contained" onClick={handleAdd}  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Add New Bookmark
            </Button>
          </PermissionGuard>
          <PermissionGuard resource="bookmark" action="view">
            <CSVLink
              data={csvData}
              headers={headers}
              filename="bookmarks.csv"
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Export CSV
              </Button>
            </CSVLink>
          </PermissionGuard>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Search bookmarks..."
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .MuiInputLabel-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      />

      {filteredBookmarks.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {bookmarks.length === 0
            ? "No bookmarks found. Add your first bookmark to get started."
            : "No bookmarks match your search criteria."}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Vehicle Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Custom ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Vehicle License</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Vehicle Color</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Business Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBookmarks.map((bookmark) => (
                <TableRow key={bookmark.id}>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {vehicleTypeMap[bookmark.vehicleType] || "Unknown"}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{bookmark.customId}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{bookmark.vlicensePlate || 'N/A'}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{bookmark.vcolor || 'N/A'}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{bookmark.vbusinessName || 'N/A'}</TableCell>
                  <TableCell>
                    <PermissionGuard resource="bookmark" action="edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(bookmark.id)}
                        title="Edit Bookmark"

                      >
                        <EditIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                    <PermissionGuard resource="bookmark" action="view">
                      <IconButton
                        color="primary"
                        onClick={() => handleProfile(bookmark.id)}
                        title="View Profile"

                      >
                        <AssignmentIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                    <PermissionGuard resource="bookmark" action="delete">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDialog(bookmark.id)}
                        title="Delete Bookmark"

                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog with Tracker Check */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
        title="Delete Bookmark"
        message={`Are you sure you want to delete bookmark "${deleteDialog.customId}"?`}
        warning={
          deleteDialog.hasTracker
            ? `⚠️ This bookmark has ${deleteDialog.trackerCount} linked tracker(s): ${deleteDialog.trackerNames.join(', ')}. The tracker(s) will be automatically unlinked before deletion.`
            : null
        }
        severity={deleteDialog.hasTracker ? 'error' : 'warning'}
        loading={deleteLoading}
        confirmText="Delete Bookmark"
        confirmColor="error"
      />
    </Container>
  );
};

export default BookmarkList;
